// TC1: Cinematic reveal — mountains rise, title cuts in like a blade
import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill, spring } from "remotion";
import { loadFont as loadMontserrat } from "@remotion/google-fonts/MontserratAlternates";
import { loadFont as loadNoto } from "@remotion/google-fonts/NotoSansDevanagari";

const { fontFamily: mont } = loadMontserrat("normal", { weights: ["900"], subsets: ["latin"] });
const { fontFamily: noto } = loadNoto("normal", { weights: ["400"], subsets: ["devanagari"] });

const MountainSilhouette: React.FC = () => (
  <svg width="1920" height="400" viewBox="0 0 1920 400" preserveAspectRatio="none" fill="none">
    <path d="M0 400 L0 280 L120 200 L200 240 L320 140 L440 220 L520 120 L600 180 L680 80 L760 160 L840 60 L920 140 L1000 40 L1080 130 L1160 70 L1240 150 L1320 90 L1400 170 L1480 110 L1560 200 L1640 130 L1720 220 L1800 160 L1880 240 L1920 200 L1920 400 Z"
      fill="rgba(10,8,14,0.95)" />
    {/* Snow caps */}
    <path d="M840 60 L820 90 L860 90 Z" fill="rgba(255,255,255,0.7)" />
    <path d="M1000 40 L975 80 L1025 80 Z" fill="rgba(255,255,255,0.85)" />
    <path d="M1160 70 L1140 100 L1180 100 Z" fill="rgba(255,255,255,0.65)" />
    <path d="M520 120 L502 148 L538 148 Z" fill="rgba(255,255,255,0.5)" />
    {/* Ridge texture lines */}
    <path d="M760 160 L840 60 L920 140" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    <path d="M920 140 L1000 40 L1080 130" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
  </svg>
);

const TempleSilhouette: React.FC = () => (
  <svg width="120" height="100" viewBox="0 0 110 120" fill="none">
    <rect x="5" y="100" width="100" height="10" rx="1" fill="rgba(255,215,0,0.6)" />
    <rect x="18" y="72" width="74" height="30" rx="1" fill="rgba(255,215,0,0.5)" />
    <path d="M42 102 L42 82 Q55 72 68 82 L68 102Z" fill="rgba(0,0,0,0.4)" />
    <path d="M12 72 L98 72 L88 58 L22 58Z" fill="rgba(255,215,0,0.5)" />
    <path d="M22 58 L88 58 L80 44 L30 44Z" fill="rgba(255,215,0,0.45)" />
    <path d="M32 44 L78 44 L55 10Z" fill="rgba(255,215,0,0.5)" />
    <circle cx="55" cy="8" r="4" fill="rgba(255,215,0,0.8)" />
  </svg>
);

export const KedarnathTC1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Sky gradient reveal
  const skyOpacity = interpolate(frame, [0, Math.round(0.5 * fps)], [0, 1], { extrapolateRight: "clamp", extrapolateLeft: "clamp" });

  // Mountains rise from bottom
  const mountY = interpolate(frame, [0, Math.round(1.2 * fps)], [120, 0], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  // Title blade wipe left→right
  const titleClip = interpolate(frame, [Math.round(0.8 * fps), Math.round(1.6 * fps)], [0, 100], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.4, 0, 0.2, 1),
  });

  // Subtitle fade
  const subOpacity = interpolate(frame, [Math.round(1.8 * fps), Math.round(2.5 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });

  // Temple + tagline
  const tagOpacity = interpolate(frame, [Math.round(2.2 * fps), Math.round(3 * fps)], [0, 1], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
  });

  // Golden line under title
  const lineWidth = interpolate(frame, [Math.round(1.6 * fps), Math.round(2.2 * fps)], [0, 100], {
    extrapolateRight: "clamp", extrapolateLeft: "clamp",
    easing: Easing.bezier(0.4, 0, 0.2, 1),
  });

  // Fade out
  const fadeOut = interpolate(frame, [Math.round(4.2 * fps), Math.round(5 * fps)], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const S = 1280 / 1920;
  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, width: 1920, height: 1080, transform: `scale(${S})`, transformOrigin: 'top left', opacity: fadeOut, background: "linear-gradient(180deg, #0a0614 0%, #160d24 40%, #1a0f1a 70%, #0d0a10 100%)" }}>
      {/* Stars */}
      {[...Array(40)].map((_, i) => (
        <div key={i} style={{
          position: "absolute",
          left: `${(i * 137.5) % 100}%`,
          top: `${(i * 97.3) % 55}%`,
          width: i % 5 === 0 ? 3 : 1.5,
          height: i % 5 === 0 ? 3 : 1.5,
          borderRadius: "50%",
          background: `rgba(255,255,255,${0.3 + (i % 4) * 0.15})`,
          opacity: skyOpacity,
        }} />
      ))}

      {/* Subtle aurora/mist */}
      <div style={{
        position: "absolute", top: "15%", left: "20%", right: "20%", height: "25%",
        background: "radial-gradient(ellipse, rgba(100,60,180,0.12) 0%, transparent 70%)",
        opacity: skyOpacity,
      }} />

      {/* Mountain silhouette rises */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, transform: `translateY(${mountY}px)` }}>
        <MountainSilhouette />
      </div>

      {/* Center content */}
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginBottom: 60 }}>

        {/* Temple icon + tagline */}
        <div style={{ opacity: tagOpacity, display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <TempleSilhouette />
          <span style={{ color: "rgba(255,215,0,0.7)", fontSize: 18, fontWeight: 400, fontFamily: noto, letterSpacing: 4, textTransform: "uppercase" }}>
            द्वादश ज्योतिर्लिंग
          </span>
        </div>

        {/* KEDARNATH — blade wipe */}
        <div style={{ overflow: "hidden", position: "relative" }}>
          <div style={{
            clipPath: `inset(0 ${100 - titleClip}% 0 0)`,
            fontSize: 140,
            fontWeight: 900,
            fontFamily: mont,
            color: "#fff",
            letterSpacing: 18,
            textTransform: "uppercase",
            lineHeight: 1,
            textShadow: "0 0 80px rgba(255,215,0,0.2)",
          }}>
            KEDARNATH
          </div>
        </div>

        {/* Golden underline */}
        <div style={{
          width: `${lineWidth}%`, maxWidth: 700, height: 2,
          background: "linear-gradient(90deg, transparent, #FFD700, transparent)",
          marginTop: 4, marginBottom: 12,
        }} />

        {/* Hindi subtitle */}
        <div style={{ opacity: subOpacity, color: "rgba(255,215,0,0.85)", fontSize: 28, fontFamily: noto, fontWeight: 400, letterSpacing: 8 }}>
          केदारनाथ
        </div>
      </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
